import type { RidePickupPoint } from '@/services/platform/data/booking/rides/pickupPoints';

const GOOGLE_PLACES_SOURCE = 'google_places';

// `Place.fetchFields` has no `sessionToken` parameter of its own — the token only
// carries over automatically via `PlacePrediction.toPlace()` (see Google's docs on
// `AutocompleteSuggestion.fetchAutocompleteSuggestions`). Caching the predictions
// from the last `searchPlaces` call lets `fetchPlaceDetails` reuse `.toPlace()`
// instead of reconstructing a bare `Place` from just the ID, which would lose the
// session-token billing bundle.
const predictionsByPlaceId = new Map<string, google.maps.places.PlacePrediction>();

function getAddressComponent(
  components: google.maps.places.AddressComponent[] | undefined,
  type: string,
  field: 'longText' | 'shortText' = 'longText'
): string | null {
  const component = components?.find(c => c.types.includes(type));
  return component ? (component[field] ?? null) : null;
}

// `locality` is absent for many real addresses (rural areas, some villages), where Google
// instead reports the settlement under one of these broader types — checked in descending
// order of specificity so `RidePickupPoint.city` is populated whenever any of them is present.
const CITY_COMPONENT_TYPES = [
  'locality',
  'postal_town',
  'administrative_area_level_3',
  'sublocality',
  'sublocality_level_1',
  'administrative_area_level_2',
  'administrative_area_level_1',
];

function getCity(
  components: google.maps.places.AddressComponent[] | undefined
): { type: string; value: string } | null {
  for (const type of CITY_COMPONENT_TYPES) {
    const value = getAddressComponent(components, type);
    if (value) return { type, value };
  }
  return null;
}

/**
 * Resolves the city name in `language` by reverse-geocoding `location`.
 *
 * Google localises every address component of a street-level result in the language of its
 * first component (the street), so a Place's `locality` comes back as "Lisboa" even for `en`.
 * Reverse geocoding returns, among others, a result for the city itself — where the city *is*
 * the first component — so its name honours `language` ("Lisbon").
 *
 * @param location - Coordinates of the resolved place.
 * @param cityType - The address component type `getCity` matched (e.g. `"locality"`).
 * @param language - ISO 639-1 language code the city name should come back in.
 * @returns The localised city name, or `undefined` if no matching result is found or the request fails.
 */
async function getLocalisedCity(
  location: google.maps.LatLng,
  cityType: string,
  language: string
): Promise<string | undefined> {
  try {
    const geocoder = new google.maps.Geocoder();
    const { results } = await geocoder.geocode({ location, language });
    const cityResult = results.find(result => result.types.includes(cityType));
    return cityResult?.address_components.find(c => c.types.includes(cityType))?.long_name;
  } catch (error) {
    console.warn(`getLocalisedCity: reverse geocode failed for ${location.toUrlValue()}:`, error);
    return undefined;
  }
}

/**
 * Creates a new Autocomplete session token for `searchPlaces`/`fetchPlaceDetails`.
 *
 * `AutocompleteSessionToken` lives on the `places` library, which — unlike
 * `Geocoder` — is not part of the Maps JS base bundle: it only exists on
 * `google.maps.places` once `google.maps.importLibrary('places')` has resolved
 * at least once. Callers must await this rather than constructing the token
 * from a synchronous `google.maps.places` check, which can never come true.
 *
 * @returns A new session token, or `undefined` if the Maps script isn't loaded yet.
 */
export async function createSessionToken(): Promise<google.maps.places.AutocompleteSessionToken | undefined> {
  if (typeof google === 'undefined' || !google.maps?.importLibrary) {
    console.warn('createSessionToken: Google Maps script not loaded yet');
    return undefined;
  }

  const { AutocompleteSessionToken } = (await google.maps.importLibrary(
    'places'
  )) as google.maps.PlacesLibrary;
  return new AutocompleteSessionToken();
}

/**
 * Resolves free-text input to a list of Portugal-only address predictions using
 * the Google Places Autocomplete (New) API.
 *
 * Requires the Maps script to already be loaded (any mounted `RideMapBackdrop`
 * loads it via `APIProvider`); the `places` library itself is imported lazily
 * here via `google.maps.importLibrary`, mirroring `geocodeAddress.ts`.
 *
 * Predictions come back as partial `RidePickupPoint`s with `lat`/`lng` unset —
 * call `fetchPlaceDetails` with `referenceId` (the place ID) on selection to
 * resolve coordinates and full address fields.
 *
 * @param query - Free-text address search input.
 * @param sessionToken - Shared across the autocomplete calls for one search
 *   session so Google bills them together (and, via `toPlace()`, bundles in the
 *   follow-up `fetchPlaceDetails` call for whichever prediction gets selected).
 * @param language - ISO 639-1 language code (e.g. `"pt"`) the predictions'
 *   text should come back in — pass `getLanguageFromLocale(locale)`. Defaults
 *   to the browser's language preference when omitted.
 * @returns Matching predictions, or `[]` if the Maps script isn't loaded yet or the request fails.
 */
export async function searchPlaces(
  query: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
  language?: string
): Promise<RidePickupPoint[]> {
  if (typeof google === 'undefined' || !google.maps?.importLibrary) {
    console.warn('searchPlaces: Google Maps script not loaded yet');
    return [];
  }

  try {
    const { AutocompleteSuggestion } = (await google.maps.importLibrary(
      'places'
    )) as google.maps.PlacesLibrary;

    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
      includedRegionCodes: ['pt'],
      sessionToken,
      language,
    });

    predictionsByPlaceId.clear();

    return suggestions
      .filter(suggestion => !!suggestion.placePrediction)
      .map(({ placePrediction }) => {
        const prediction = placePrediction!;
        predictionsByPlaceId.set(prediction.placeId, prediction);

        return {
          name: prediction.mainText?.text ?? prediction.text.text,
          city: null,
          address: prediction.secondaryText?.text ?? null,
          postalCode: null,
          country: null,
          countryCode: null,
          lat: null,
          lng: null,
          source: GOOGLE_PLACES_SOURCE,
          referenceId: prediction.placeId,
        };
      });
  } catch (error) {
    console.warn(`searchPlaces: request failed for query "${query}":`, error);
    return [];
  }
}

/**
 * Resolves a Google Places prediction (by place ID) into a full `RidePickupPoint`
 * with coordinates and structured address fields, using the Places (New) `Place` API.
 *
 * @param placeId - The `referenceId` of a prediction returned by a prior `searchPlaces` call.
 * @param language - ISO 639-1 language code (e.g. `"pt"`) the resolved fields
 *   (name, formatted address, and address components — including `city`)
 *   should come back in — pass `getLanguageFromLocale(locale)`. Defaults to
 *   the browser's language preference when omitted. `Place.requestedLanguage`
 *   is only settable at construction time (assigning it post-construction
 *   throws at runtime, despite the type declarations allowing it), so passing
 *   a `language` here builds a fresh `Place` from the ID instead of going
 *   through `prediction.toPlace()` — which forgoes that call's session-token
 *   billing bundle (see the `predictionsByPlaceId` comment above) in exchange
 *   for the translated fields. `name` and `address` are taken from the cached
 *   prediction (see the inline comment on the return value for why).
 * @returns The resolved point, or `undefined` if the prediction isn't cached
 *   (e.g. the query changed since it was fetched), the place has no location,
 *   or the request fails.
 */
export async function fetchPlaceDetails(
  placeId: string,
  language?: string
): Promise<RidePickupPoint | undefined> {
  const prediction = predictionsByPlaceId.get(placeId);
  if (!prediction) {
    console.warn(`fetchPlaceDetails: no cached prediction for place "${placeId}"`);
    return undefined;
  }

  try {
    let place: google.maps.places.Place;
    if (language) {
      const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
      place = new Place({ id: placeId, requestedLanguage: language });
    } else {
      place = prediction.toPlace();
    }

    await place.fetchFields({
      fields: ['location', 'addressComponents', 'formattedAddress', 'displayName'],
    });

    const location = place.location;
    if (!location) {
      console.warn(`fetchPlaceDetails: no location for place "${placeId}"`);
      return undefined;
    }

    const components = place.addressComponents ?? undefined;
    const city = getCity(components);
    const localisedCity =
      city && language ? await getLocalisedCity(location, city.type, language) : undefined;

    // Google returns street-level details (and every address component after them) in the
    // local language regardless of `requestedLanguage` — e.g. "Lisboa" for `en` — whereas the
    // autocomplete prediction text honours it ("Lisbon"). Display fields therefore come from
    // the prediction, and `city` from a localised reverse geocode (see `getLocalisedCity`).
    return {
      name: prediction.mainText?.text ?? place.displayName ?? place.formattedAddress ?? '',
      city: localisedCity ?? city?.value ?? null,
      address: prediction.text.text || place.formattedAddress || null,
      postalCode: getAddressComponent(components, 'postal_code'),
      country: getAddressComponent(components, 'country'),
      countryCode: getAddressComponent(components, 'country', 'shortText'),
      lat: location.lat(),
      lng: location.lng(),
      source: GOOGLE_PLACES_SOURCE,
      referenceId: placeId,
    };
  } catch (error) {
    console.warn(`fetchPlaceDetails: request failed for place "${placeId}":`, error);
    return undefined;
  }
}
